import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, tap, map, switchMap } from 'rxjs/operators';
import { ThemeRepository } from './theme-repository.service';
import { ThemeStore } from './theme-store.service';
import { ThemeEventBus } from './theme-event-bus.service';
import {
  generatePrimaryActiveColor,
  generatePrimaryHoverColor
} from '../utils/theme-primary-lightness.util';

export interface ThemeColors {
  // Primary Colors
  primary_default?: string;
  primary_hover?: string;
  primary_active?: string;

  // Status Colors
  feedback_error?: string;
  feedback_success?: string;
  feedback_warning?: string;

  // Gray Scale
  gray_100?: string;
  gray_200?: string;
  gray_500?: string;
  gray_700?: string;
  gray_800?: string;

  // Background Colors
  background_white?: string;

  // Text Colors
  text_primary?: string;
  text_invert?: string;
  text_secondary?: string;
  text_accent?: string;

  // Border Colors
  border_accent?: string;
}

export interface ButtonConfig {
  id?: number;
  theme?: number;
  button_type: 'default' | 'ghost' | 'ghost2' | 'confirm' | 'delete';
  background_color: string;
  text_color: string;
  border_color?: string;
  hover_background_color?: string;
  hover_text_color?: string;
  hover_border_color?: string;
  active_background_color?: string;
  active_text_color?: string;
  active_border_color?: string;
  border_radius?: number;
  padding_x?: number;
  padding_y?: number;
  font_size?: number;
  font_weight?: string;
}

export interface ThemeWithButtons {
  theme: ThemeColors;
  buttonConfigs: ButtonConfig[];
}

export interface DefaultThemeColors {
  primary_default: string;
  primary_hover: string;
  primary_active: string;
  feedback_error: string;
  feedback_success: string;
  feedback_warning: string;
  gray_100: string;
  gray_200: string;
  gray_500: string;
  gray_700: string;
  gray_800: string;
  background_white: string;
  text_primary: string;
  text_invert: string;
  text_secondary: string;
  text_accent: string;
  border_accent: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private renderer: Renderer2;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private themeRepository: ThemeRepository,
    private themeStore: ThemeStore,
    private eventBus: ThemeEventBus
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.setupEventListeners();
  }

  /**
   * Setup event listeners for loose coupling
   */
  private setupEventListeners(): void {
    // Listen for color updates from other services
    this.eventBus.onColorUpdate().subscribe(event => {
      console.log('🎨 ThemeService: Received color update event', event.payload);
      this.applyThemeColors(event.payload);
    });

    // Listen for theme loaded events
    this.eventBus.onThemeLoaded().subscribe(event => {
      console.log('🎨 ThemeService: Received theme loaded event', event.payload);
      this.applyThemeColors(event.payload);
    });

    // Listen for theme errors
    this.eventBus.onThemeError().subscribe(event => {
      console.log('🎨 ThemeService: Received theme error event', event.payload);
      this.applyThemeColors(event.payload.fallbackColors);
    });
  }

  /**
   * Fetch theme colors from backend
   */
  getThemeColors(): Observable<ThemeColors> {
    console.log('🚀 ThemeService: Fetching theme colors...');

    this.themeStore.setLoading(true);

    return this.themeRepository.getActiveThemeConfig().pipe(
      map(response => {
        console.log('📡 ThemeService: Raw API response:', response);
        const mappedColors = this.mapApiResponseToThemeColors(response);
        console.log('🔄 ThemeService: Mapped colors:', mappedColors);
        return mappedColors;
      }),
      tap(colors => {
        console.log('💾 ThemeService: Storing colors in store:', colors);
        this.themeStore.updateColors(colors);

        // Apply colors to CSS variables immediately for real-time updates
        console.log('🎨 ThemeService: Applying theme colors to CSS variables during initialization');
        this.applyThemeColors(colors);

        this.eventBus.emitThemeLoaded(colors);
      }),
      catchError(error => {
        console.error('❌ ThemeService: Error fetching theme colors:', error);
        const defaultColors = this.themeStore.getCurrentColors();
        this.themeStore.setError('Failed to load theme colors');
        this.eventBus.emitThemeError(error, defaultColors);
        return of(defaultColors);
      }),
      tap(() => {
        this.themeStore.setLoading(false);
      })
    );
  }

  /**
   * Fetch complete theme (colors + button configs) from backend for initialization
   */
  getCompleteTheme(): Observable<{colors: ThemeColors, buttonConfigs: ButtonConfig[]}> {
    console.log('🚀 ThemeService: Fetching complete theme (colors + button configs)...');

    this.themeStore.setLoading(true);

    return this.themeRepository.getActiveThemeConfig().pipe(
      switchMap(themeResponse => {
        console.log('📡 ThemeService: Raw theme API response:', themeResponse);

        // Extract theme colors
        const mappedColors = this.mapApiResponseToThemeColors(themeResponse);
        console.log('🔄 ThemeService: Mapped colors:', mappedColors);

        // Extract button configurations if available
        let buttonConfigs: ButtonConfig[] = [];
        if (themeResponse && themeResponse.button_configs) {
          buttonConfigs = themeResponse.button_configs;
          console.log('🔘 ThemeService: Found button configs in theme response:', buttonConfigs);
        } else {
          // If no button configs in theme response, fetch them separately
          console.log('🔘 ThemeService: No button configs in theme response, fetching separately...');
          return this.themeRepository.getButtonConfigs().pipe(
            map(buttonConfigsResponse => {
              console.log('🔘 ThemeService: Button configs fetched separately:', buttonConfigsResponse);
              return { colors: mappedColors, buttonConfigs: buttonConfigsResponse };
            })
          );
        }

        return of({ colors: mappedColors, buttonConfigs });
      }),
      tap(({colors, buttonConfigs}) => {
        console.log('💾 ThemeService: Storing complete theme in store:', {colors, buttonConfigs});
        this.themeStore.updateColors(colors);
        this.themeStore.updateButtonConfigs(buttonConfigs);

        // Apply complete theme to CSS variables immediately for real-time updates
        console.log('🎨 ThemeService: Applying complete theme to CSS variables during initialization');
        this.applyCompleteTheme(colors, buttonConfigs);

        this.eventBus.emitThemeLoaded(colors);
      }),
      catchError(error => {
        console.error('❌ ThemeService: Error fetching complete theme:', error);
        const defaultColors = this.themeStore.getCurrentColors();
        const defaultButtonConfigs = this.themeStore.getCurrentButtonConfigs();
        this.themeStore.setError('Failed to load complete theme');
        this.eventBus.emitThemeError(error, defaultColors);
        return of({ colors: defaultColors, buttonConfigs: defaultButtonConfigs });
      }),
      tap(() => {
        this.themeStore.setLoading(false);
      })
    );
  }

  /**
   * Apply theme colors to CSS custom properties
   */
  applyThemeColors(colors: ThemeColors): void {
    const root = this.document.documentElement;

    console.log('🎯 ThemeService: Applying theme colors to CSS variables:', colors);

    // Apply CSS custom properties for theme colors
    console.log('🔧 ThemeService: Setting CSS variables:');
    console.log('  Setting --theme-primary to:', colors.primary_default);

    // Force set CSS variables using direct DOM manipulation as fallback
    root.style.setProperty('--theme-primary', colors.primary_default);
    root.style.setProperty('--theme-primary-hover', colors.primary_hover);
    root.style.setProperty('--theme-primary-active', colors.primary_active);

    console.log('  Setting --theme-success to:', colors.feedback_success);
    root.style.setProperty('--theme-danger', colors.feedback_error);
    root.style.setProperty('--theme-success', colors.feedback_success);
    root.style.setProperty('--theme-warning', colors.feedback_warning);

    // Also use renderer as backup
    this.renderer.setStyle(root, '--theme-primary', colors.primary_default);
    this.renderer.setStyle(root, '--theme-primary-hover', colors.primary_hover);
    this.renderer.setStyle(root, '--theme-primary-active', colors.primary_active);
    this.renderer.setStyle(root, '--theme-danger', colors.feedback_error);
    this.renderer.setStyle(root, '--theme-success', colors.feedback_success);
    this.renderer.setStyle(root, '--theme-warning', colors.feedback_warning);

    this.renderer.setStyle(root, '--theme-gray-100', colors.gray_100);
    this.renderer.setStyle(root, '--theme-gray-200', colors.gray_200);
    this.renderer.setStyle(root, '--theme-gray-500', colors.gray_500);
    this.renderer.setStyle(root, '--theme-gray-700', colors.gray_700);
    this.renderer.setStyle(root, '--theme-gray-800', colors.gray_800);

    this.renderer.setStyle(root, '--theme-background', colors.background_white);

    this.renderer.setStyle(root, '--theme-text-primary', colors.text_primary);
    this.renderer.setStyle(root, '--theme-text-inverse', colors.text_invert);
    this.renderer.setStyle(root, '--theme-text-secondary', colors.text_secondary);
    this.renderer.setStyle(root, '--theme-text-accent', colors.text_accent);

    this.renderer.setStyle(root, '--theme-border-accent', colors.border_accent);

    // Debug: Log the actual CSS variable values after setting them
    // Use setTimeout to ensure DOM has been updated
    setTimeout(() => {
      const computedStyle = this.document.defaultView?.getComputedStyle(root);
      console.log('🔍 ThemeService: CSS variable values after update:');
      console.log('  --theme-primary:', computedStyle?.getPropertyValue('--theme-primary'));
      console.log('  --theme-success:', computedStyle?.getPropertyValue('--theme-success'));
      console.log('  --theme-danger:', computedStyle?.getPropertyValue('--theme-danger'));

      // Also log what we actually set
      console.log('🎯 ThemeService: Values we set:');
      console.log('  primary_default:', colors.primary_default);
      console.log('  feedback_success:', colors.feedback_success);
      console.log('  feedback_error:', colors.feedback_error);
    }, 0);

    console.log('✅ ThemeService: Theme colors applied successfully!');
  }

  /**
   * Apply button colors to CSS custom properties for real-time updates
   */
  applyButtonColors(buttonConfigs: ButtonConfig[]): void {
    const root = this.document.documentElement;

    console.log('🔘 ThemeService: Applying button colors to CSS variables:', buttonConfigs);

    buttonConfigs.forEach(config => {
      const buttonType = config.button_type;

      // Apply button colors based on type using direct DOM manipulation
      switch (buttonType) {
        case 'default':
          console.log(`🔘 Setting default button colors:`, config);
          root.style.setProperty('--btn-default-bg', config.background_color);
          root.style.setProperty('--btn-default-text', config.text_color);
          root.style.setProperty('--btn-default-hover-bg', config.hover_background_color || this.generateHoverColor(config.background_color));
          root.style.setProperty('--btn-default-hover-text', config.hover_text_color || config.text_color);
          root.style.setProperty('--btn-default-active-bg', config.active_background_color || this.generateActiveColor(config.background_color));
          root.style.setProperty('--btn-default-active-text', config.active_text_color || config.text_color);
          break;

        case 'ghost':
          console.log(`🔘 Setting ghost button colors:`, config);
          root.style.setProperty('--btn-ghost-bg', config.background_color);
          root.style.setProperty('--btn-ghost-text', config.text_color);
          root.style.setProperty('--btn-ghost-border', config.border_color || config.text_color);
          root.style.setProperty('--btn-ghost-hover-bg', config.hover_background_color || config.text_color);
          root.style.setProperty('--btn-ghost-hover-text', config.hover_text_color || config.text_color);
          root.style.setProperty('--btn-ghost-active-bg', config.active_background_color || this.generateActiveColor(config.text_color));
          root.style.setProperty('--btn-ghost-active-text', config.active_text_color || config.text_color);
          break;

        case 'ghost2':
          console.log(`🔘 Setting ghost2 button colors:`, config);
          root.style.setProperty('--btn-ghost2-text', config.text_color);
          root.style.setProperty('--btn-ghost2-hover-text', config.hover_text_color || config.text_color);
          root.style.setProperty('--btn-ghost2-active-text', config.active_text_color || config.text_color);
          break;

        case 'confirm':
          console.log(`🔘 Setting confirm button colors:`, config);
          root.style.setProperty('--btn-confirm-bg', config.background_color);
          root.style.setProperty('--btn-confirm-text', config.text_color);
          root.style.setProperty('--btn-confirm-hover-bg', config.hover_background_color || this.generateHoverColor(config.background_color));
          root.style.setProperty('--btn-confirm-hover-text', config.hover_text_color || config.text_color);
          root.style.setProperty('--btn-confirm-active-bg', config.active_background_color || this.generateActiveColor(config.background_color));
          root.style.setProperty('--btn-confirm-active-text', config.active_text_color || config.text_color);
          break;

        case 'delete':
          console.log(`🔘 Setting delete button colors:`, config);
          root.style.setProperty('--btn-delete-bg', config.background_color);
          root.style.setProperty('--btn-delete-text', config.text_color);
          root.style.setProperty('--btn-delete-hover-bg', config.hover_background_color || this.generateHoverColor(config.background_color));
          root.style.setProperty('--btn-delete-hover-text', config.hover_text_color || config.text_color);
          root.style.setProperty('--btn-delete-active-bg', config.active_background_color || this.generateActiveColor(config.background_color));
          root.style.setProperty('--btn-delete-active-text', config.active_text_color || config.text_color);
          break;
      }
    });

    console.log('✅ ThemeService: Button colors applied successfully!');
  }

  /**
   * Apply both theme colors and button colors for complete real-time updates
   */
  applyCompleteTheme(colors: ThemeColors, buttonConfigs: ButtonConfig[]): void {
    console.log('🎨 ThemeService: Applying complete theme (colors + buttons) for real-time updates');
    this.applyThemeColors(colors);
    this.applyButtonColors(buttonConfigs);

    // Emit event to notify all components of the update
    this.eventBus.emitColorUpdate(colors);
    console.log('🚀 ThemeService: Real-time theme update completed - all components should reflect changes!');

    // Test CSS variables after a short delay
    setTimeout(() => {
      this.testCSSVariables();
    }, 100);
  }

  /**
   * Test method to verify CSS variables are working correctly
   */
  testCSSVariables(): void {
    const root = this.document.documentElement;
    const computedStyle = this.document.defaultView?.getComputedStyle(root);

    console.log('🧪 ThemeService: Testing CSS variables:');
    console.log('  --theme-primary:', computedStyle?.getPropertyValue('--theme-primary'));
    console.log('  --theme-success:', computedStyle?.getPropertyValue('--theme-success'));
    console.log('  --theme-danger:', computedStyle?.getPropertyValue('--theme-danger'));

    // Check if values match what we expect
    const primaryValue = computedStyle?.getPropertyValue('--theme-primary').trim();
    const successValue = computedStyle?.getPropertyValue('--theme-success').trim();

    if (primaryValue && primaryValue !== '#63CAE5') {
      console.log('✅ ThemeService: CSS variables are working! Primary color updated to:', primaryValue);
    } else {
      console.log('❌ ThemeService: CSS variables may not be working. Primary still default:', primaryValue);

      // Debug: Check if CSS variables are actually set on the element
      console.log('🔍 ThemeService: Debugging CSS variable issue...');
      console.log('  Direct style property --theme-primary:', root.style.getPropertyValue('--theme-primary'));
      console.log('  Computed style --theme-primary:', computedStyle?.getPropertyValue('--theme-primary'));

      // Try to force set again using the current theme color
      console.log('🔧 ThemeService: Attempting to force set CSS variable...');
      const currentColors = this.themeStore.getCurrentColors();
      root.style.setProperty('--theme-primary', currentColors.primary_default, 'important');

      // Check again after force set
      setTimeout(() => {
        const newComputedStyle = this.document.defaultView?.getComputedStyle(root);
        console.log('🔍 ThemeService: After force set with !important:');
        console.log('  --theme-primary:', newComputedStyle?.getPropertyValue('--theme-primary'));
      }, 10);
    }
  }

  /**
   * Force refresh CSS variables - useful for debugging
   */
  forceRefreshCSSVariables(): void {
    console.log('🔄 ThemeService: Force refreshing CSS variables...');
    const colors = this.themeStore.getCurrentColors();
    const buttonConfigs = this.themeStore.getCurrentButtonConfigs();

    console.log('🎯 ThemeService: Current colors from store:', colors);
    console.log('🔘 ThemeService: Current button configs from store:', buttonConfigs);

    this.applyCompleteTheme(colors, buttonConfigs);
  }

  /**
   * Manual CSS variable test - call this from browser console
   */
  manualCSSTest(): void {
    console.log('🧪 ThemeService: Manual CSS variable test...');
    const root = this.document.documentElement;

    // Test 1: Direct style property
    console.log('Test 1: Direct style property');
    root.style.setProperty('--theme-primary', '#FF0000');
    console.log('Set --theme-primary to #FF0000');

    setTimeout(() => {
      const computedStyle = this.document.defaultView?.getComputedStyle(root);
      console.log('Result:', computedStyle?.getPropertyValue('--theme-primary'));
    }, 100);

    // Test 2: With !important
    setTimeout(() => {
      console.log('Test 2: With !important');
      root.style.setProperty('--theme-primary', '#00FF00', 'important');
      console.log('Set --theme-primary to #00FF00 with !important');

      setTimeout(() => {
        const computedStyle = this.document.defaultView?.getComputedStyle(root);
        console.log('Result:', computedStyle?.getPropertyValue('--theme-primary'));
      }, 100);
    }, 200);

    // Test 3: Check if pagination updates
    setTimeout(() => {
      console.log('Test 3: Check if pagination component updates');
      const paginationElements = this.document.querySelectorAll('.page-link');
      paginationElements.forEach((el, index) => {
        const computedStyle = this.document.defaultView?.getComputedStyle(el);
        console.log(`Pagination element ${index} background-color:`, computedStyle?.getPropertyValue('background-color'));
      });
    }, 500);
  }

  /**
   * Test button color updates across the application
   */
  testButtonColors(): void {
    console.log('🔘 ThemeService: Testing button color updates...');
    const root = this.document.documentElement;

    // Test 1: Set default button to red
    console.log('Test 1: Setting default button to red');
    root.style.setProperty('--btn-default-bg', '#FF0000');
    root.style.setProperty('--btn-default-text', '#FFFFFF');

    setTimeout(() => {
      // Test 2: Set confirm button to green
      console.log('Test 2: Setting confirm button to green');
      root.style.setProperty('--btn-confirm-bg', '#00FF00');
      root.style.setProperty('--btn-confirm-text', '#000000');

      setTimeout(() => {
        // Test 3: Set delete button to blue
        console.log('Test 3: Setting delete button to blue');
        root.style.setProperty('--btn-delete-bg', '#0000FF');
        root.style.setProperty('--btn-delete-text', '#FFFFFF');

        setTimeout(() => {
          // Test 4: Check if buttons updated
          console.log('Test 4: Checking button elements');
          const defaultButtons = this.document.querySelectorAll('.btn--default, .btn--primary');
          const confirmButtons = this.document.querySelectorAll('.btn--confirm, .btn--success');
          const deleteButtons = this.document.querySelectorAll('.btn--cancel, .btn--delete, .btn--danger');

          console.log(`Found ${defaultButtons.length} default buttons`);
          console.log(`Found ${confirmButtons.length} confirm buttons`);
          console.log(`Found ${deleteButtons.length} delete buttons`);

          // Check computed styles
          defaultButtons.forEach((btn, index) => {
            const style = this.document.defaultView?.getComputedStyle(btn);
            console.log(`Default button ${index} background:`, style?.getPropertyValue('background-color'));
          });

          confirmButtons.forEach((btn, index) => {
            const style = this.document.defaultView?.getComputedStyle(btn);
            console.log(`Confirm button ${index} background:`, style?.getPropertyValue('background-color'));
          });

          deleteButtons.forEach((btn, index) => {
            const style = this.document.defaultView?.getComputedStyle(btn);
            console.log(`Delete button ${index} background:`, style?.getPropertyValue('background-color'));
          });

          // Restore original colors
          setTimeout(() => {
            console.log('Restoring original button colors...');
            const colors = this.themeStore.getCurrentColors();
            const buttonConfigs = this.themeStore.getCurrentButtonConfigs();
            this.applyCompleteTheme(colors, buttonConfigs);
          }, 1000);

        }, 500);
      }, 500);
    }, 500);
  }

  /**
   * Get current theme colors
   */
  getCurrentThemeColors(): ThemeColors {
    return this.themeStore.getCurrentColors();
  }

  /**
   * Get theme colors as observable
   */
  getThemeColors$(): Observable<ThemeColors> {
    return this.themeStore.getColors$();
  }

  /**
   * Update a specific color and apply it immediately
   */
  updateColor(key: keyof ThemeColors, value: string): void {
    this.themeStore.updateSingleColor(key, value);
    const updatedColors = this.themeStore.getCurrentColors();
    this.eventBus.emitColorUpdate(updatedColors);
  }

  /**
   * Reset to default colors
   */
  resetToDefaults(): void {
    this.themeStore.reset();
    const defaultColors = this.themeStore.getCurrentColors();
    this.eventBus.emitColorUpdate(defaultColors);
  }

  /**
   * Map API response to theme colors interface
   * Adjust this method based on your actual API response structure
   */
  private mapApiResponseToThemeColors(apiResponse: any): ThemeColors {
    console.log('🔍 ThemeService: API Response received:', apiResponse);

    // This method should be customized based on your actual API response structure
    // For now, assuming the API returns colors in the expected format

    if (Array.isArray(apiResponse) && apiResponse.length > 0) {
      // If API returns an array, take the first item
      console.log('📦 ThemeService: Processing array response, taking first item:', apiResponse[0]);
      return this.mapSingleThemeConfig(apiResponse[0]);
    } else if (apiResponse && typeof apiResponse === 'object') {
      // If API returns a single object
      console.log('📄 ThemeService: Processing single object response:', apiResponse);
      return this.mapSingleThemeConfig(apiResponse);
    }

    console.log('⚠️ ThemeService: Unexpected response format, using default colors');
    // Return default colors if response format is unexpected
    return this.themeStore.getCurrentColors();
  }

  /**
   * Map a single theme config object to ThemeColors
   */
  private mapSingleThemeConfig(config: any): ThemeColors {
    console.log('🎨 ThemeService: Mapping theme config:', config);
    console.log('🔍 ThemeService: Config.primary_default value:', config.primary_default);
    console.log('🔍 ThemeService: Config keys:', Object.keys(config));

    const defaultColors = this.themeStore.getCurrentColors();
    const hasBackendThemeData = !!config && typeof config === 'object' && Object.keys(config).length > 0;

    if (!hasBackendThemeData) {
      console.warn('⚠️ ThemeService: No backend theme payload detected; using fallback defaults from ThemeStore/_theme-variables.scss.');
    } else {
      const missingCriticalFields: string[] = [];
      if (!config.primary_default) missingCriticalFields.push('primary_default');
      if (!config.primary_hover) missingCriticalFields.push('primary_hover');
      if (!config.primary_active) missingCriticalFields.push('primary_active');
      if (missingCriticalFields.length > 0) {
        console.warn('⚠️ ThemeService: Backend theme payload is partial. Missing:', missingCriticalFields);
      }
    }

    const mappedColors = {
      // If backend is empty or partial, keep design-system defaults from ThemeStore
      // (which mirror _theme-variables.scss). When primary_default exists but hover/active
      // are omitted, derive lighter steps from `_theme-variables` lightness deltas.
      primary_default: config.primary_default || defaultColors.primary_default,
      primary_hover:
        config.primary_hover ||
        (config.primary_default
          ? generatePrimaryHoverColor(config.primary_default, this.document)
          : defaultColors.primary_hover),
      primary_active:
        config.primary_active ||
        (config.primary_default
          ? generatePrimaryActiveColor(config.primary_default, this.document)
          : defaultColors.primary_active),
      feedback_error: config.feedback_error || defaultColors.feedback_error,
      feedback_success: config.feedback_success || defaultColors.feedback_success,
      feedback_warning: config.feedback_warning || defaultColors.feedback_warning,
      gray_100: config.gray_100 || defaultColors.gray_100,
      gray_200: config.gray_200 || defaultColors.gray_200,
      gray_500: config.gray_500 || defaultColors.gray_500,
      gray_700: config.gray_700 || defaultColors.gray_700,
      gray_800: config.gray_800 || defaultColors.gray_800,
      background_white: config.background_white || defaultColors.background_white,
      text_primary: config.text_primary || defaultColors.text_primary,
      text_invert: config.text_invert || defaultColors.text_invert,
      text_secondary: config.text_secondary || defaultColors.text_secondary,
      text_accent: config.text_accent || defaultColors.text_accent,
      border_accent: config.dark_accent || config.border_accent || defaultColors.border_accent // Use dark_accent from your API
    };

    console.log('✅ ThemeService: Mapped colors result:', mappedColors);
    console.log('🧪 ThemeService: Fallback usage check:', {
      usingFallbackPrimaryDefault: !config.primary_default,
      usingFallbackPrimaryHover: !config.primary_hover,
      usingFallbackPrimaryActive: !config.primary_active,
      finalPrimaryDefault: mappedColors.primary_default,
      finalPrimaryHover: mappedColors.primary_hover,
      finalPrimaryActive: mappedColors.primary_active
    });
    console.log('🎯 ThemeService: Primary default mapping:', {
      'config.primary_default': config.primary_default,
      'defaultColors.primary_default': defaultColors.primary_default,
      'final result': mappedColors.primary_default
    });
    return mappedColors;
  }

  /**
   * Generate hover color (slightly darker)
   */
  private generateHoverColor(baseColor: string): string {
    return this.adjustColorLightness(baseColor, -10);
  }

  /**
   * Generate active color (even darker)
   */
  private generateActiveColor(baseColor: string): string {
    return this.adjustColorLightness(baseColor, -20);
  }

  /**
   * Adjust color lightness
   */
  private adjustColorLightness(color: string, amount: number): string {
    // Remove # if present
    const hex = color.replace('#', '');

    // Convert hex to RGB
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Convert RGB to HSL
    const hsl = this.rgbToHsl(r, g, b);

    // Adjust lightness
    hsl[2] = Math.max(0, Math.min(100, hsl[2] + amount));

    // Convert back to hex
    return this.hslToHex(hsl[0], hsl[1], hsl[2]);
  }

  /**
   * Convert RGB to HSL
   */
  private rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return [h * 360, s * 100, l * 100];
  }

  /**
   * Convert HSL to Hex
   */
  private hslToHex(h: number, s: number, l: number): string {
    h /= 360;
    s /= 100;
    l /= 100;

    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}
